import React, { FC, useEffect } from "react";

import log from "loglevel";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom";

import { LoaderWithText } from "@/components/atoms";
import config from "@/config";
import { authApi, brokerApi, userApi } from "@/shared/apis";
import { UserState } from "@/shared/domain";
import { useAuth, useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

const ItsMeActivateAccount: FC = () => {
    const {
        search: { state: itsmeToken },
        globalState: { lang },
    } = useRouterHook();
    const { token } = useParams<any>();
    const { i18n, t } = useTranslation();
    const queryClient = useQueryClient();
    const { navToRoot } = useNavigation();
    const { prepare, authItsme, loginSilent } = useAuth();

    const handleItsmeAuthentication = async (
        itsmeUserId: string,
        authChallengeToken: string,
        email: string,
        officeId: string,
    ) => {
        try {
            // Step 1: authenticate (get cookie & token based on user/password)
            await authItsme(itsmeUserId, authChallengeToken);
            log.info("fAuthItsme success");

            // Step 2: get available broker of the user
            const availableBrokers = await brokerApi.getBrokers();
            log.debug("handleBrokers - brokers: ", availableBrokers);

            // store in cache so the home page load faster
            queryClient.setQueryData("availableBrokers", availableBrokers);

            // Step 3: auto-login
            await loginSilent(email, officeId, true);

            log.info("Itsme authentication with success");

            tagManager.pushLoginEvent("itsme");
            return;
        } catch (e) {
            log.warn("ItsmeAuthentication failed", e.message);
            navToRoot();
            if (config.NODE_ENV !== "test") {
                throw e;
            }
        }
    };

    const handleItsmeProcess = async () => {
        try {
            // Step 1: get itsme info with received token
            log.info("fItsmePost", { itsmeToken });
            const { itsmeId } = await authApi.fItsmePost(itsmeToken);
            // if the user is not registered on my broker, redirect to screen where he can link of register
            if (!itsmeId) {
                throw new Error("No itsme id received");
            }

            // Step 2: validate invite token
            log.info("itsme activate token-validation", { token });
            const { email, officeId, language } = await userApi.itsmeActivateTokenValidation({ params: { token } });
            if (!email || !officeId) {
                throw new Error("No email or office received");
            }
            // Step 3: complete
            log.info("itsme activate complete", { email });
            const { authChallengeToken } = await userApi.itsmeActivateComplete({
                params: { itsMeUid: itsmeId, email, officeId, language, token },
            });
            if (!authChallengeToken) {
                throw new Error("No auth challenge token received");
            }

            log.info("Itsme account activation with success");

            tagManager.pushSignUpEvent("itsme", "push");

            // Step 4: continue to auto-login
            prepare(UserState.ACCESS);
            await handleItsmeAuthentication(itsmeId, authChallengeToken, email, officeId);
        } catch (e) {
            navToRoot();
            if (config.NODE_ENV !== "test") {
                throw e;
            }
        }
    };

    useEffect(() => {
        if (lang) i18n.changeLanguage(lang);

        if (itsmeToken && token) {
            log.info("Itsme authentication started");
            handleItsmeProcess();
            return;
        }

        // If no tokens are received, redirect the user to the login page
        navToRoot();
    }, []);

    return <LoaderWithText text={t("ItsMe.LoadingMessage")} />;
};

export default ItsMeActivateAccount;
